import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import BEMHelper from '@ynap/bem-helper';
import { normalizeStringHelper } from '@ynap/normalize-string-helper';

const bem = new BEMHelper('CombinedSelect');

const Search = ({ value, onSearch, placeholder, handleItemKeyDown }) => {
    return (
        <div className={bem('search')}>
            <div className={bem('icon')} />
            <input
                type="text"
                value={value}
                onChange={(e) => onSearch(e.target.value)}
                placeholder={placeholder}
                className={bem('input')}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => handleItemKeyDown(e)}
                tabIndex="0"
                autoFocus
            />
        </div>
    );
};

class CombinedSelect extends PureComponent {
    listElementHeight = 40;

    constructor(props) {
        super(props);
        this.state = { open: false, messageCanTriggerDropdown: true, searchValue: '' };
        this.closeSelect = this.closeSelect.bind(this);
        this.toggle = this.toggle.bind(this);
        this.handleItemKeyDown = this.handleItemKeyDown.bind(this);
        this.handleTogglerKeyDown = this.handleTogglerKeyDown.bind(this);
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.props.noSizeNotification && !this.state.open && this.state.messageCanTriggerDropdown) {
            this.toggle();
        }

        // Focus first element in the dropdown for screen readers
        if (this.justOpened && !this.props.enableSearch) {
            this.list?.firstElementChild.focus();
            this.justOpened = false;
        }

        if (prevProps.value !== this.props.value) {
            this.updatesSelectScroll();
        }

        if (this.props.onClose && prevState.open && !this.state.open) this.props.onClose();
    }

    componentDidMount() {
        document.addEventListener('mousedown', this.closeSelect);
        this.updatesSelectScroll();

        if (!this.props.numOfListElementsDisplayed) {
            return;
        }

        this.listElementHeight = this.listLabelElement ? this.listLabelElement.offsetHeight : this.listElementHeight;
        const listDiff = this.listElementHeight - this.list.clientHeight;
        this.setState({ openedHeight: `${this.props.numOfListElementsDisplayed * this.listElementHeight + listDiff}px` });
        this.setState({ open: this.props.openOnMount });
        this.props.openOnMount && this.mobileSelect.click();
    }

    updatesSelectScroll() {
        let ul = this.list;
        const ulArray = Array.from(ul?.children || []);
        const li =
            ul?.children?.length > 0 &&
            ulArray.find((elem) => {
                const dataValue = elem.attributes['data-value'];
                return dataValue && dataValue.value === this.props.value;
            });

        if (li) {
            ul.scrollTop = li.offsetTop;
        }
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.closeSelect);
    }

    closeSelect(e) {
        const { setNoSizeNotification, noSizeNotification } = this.props;
        this.setState({ messageCanTriggerDropdown: true });
        if (this.state.open && !this.node.contains(e.target)) {
            this.setState({ open: false, messageCanTriggerDropdown: false });
            this.updatesSelectScroll();
        }
        if (noSizeNotification) {
            setNoSizeNotification(null);
        }
    }

    toggle() {
        const { onDropdown = () => {} } = this.props;
        if (!this.state.open) {
            onDropdown();
            // Mark first open action to trigger focus on first list item once rendered
            this.justOpened = true;
        } else {
            this.listLabelElement.focus();
        }

        this.setState({ messageCanTriggerDropdown: false, searchValue: '' });
        this.setState((prevState) => ({ open: !prevState.open }));
        this.updatesSelectScroll();
    }

    handleTogglerKeyDown(event) {
        const openDropdownKeys = [0, 32, 40, 38]; // Space, arrowUp, arrowDown keys
        const closeDropdownKeys = [0, 32, 27]; // Space, Escape keys
        const openDropdown = !this.state.open && openDropdownKeys.includes(event.keyCode);
        const closeDropdown = this.state.open && closeDropdownKeys.includes(event.keyCode);

        if (openDropdown || closeDropdown) {
            event.preventDefault();
            this.toggle();
        }
    }

    handleItemKeyDown(event, value = null) {
        const selectOptionKeys = [0, 32, 13]; // Space & Enter keys
        const closeDropdownKey = 27; // Escape key
        const arrowDownKey = 40;
        const arrowUpKey = 38;
        const { keyCode, target } = event;

        if (keyCode == arrowDownKey) {
            event.preventDefault();
            target.nextElementSibling ? target.nextElementSibling.focus() : this.list?.firstElementChild?.focus();
        } else if (keyCode == arrowUpKey) {
            event.preventDefault();
            target.previousElementSibling ? target.previousElementSibling.focus() : this.list?.lastElementChild?.focus();
        } else if (value && selectOptionKeys.includes(keyCode)) {
            event.preventDefault();
            this.selectOption(value);
            this.toggle();
        } else if (keyCode == closeDropdownKey) {
            this.toggle();
        }
    }

    buildOptions(options = [], currentValue, isOpened) {
        const customOptions = [];
        const nativeOptions = [];
        let selectedLabel = currentValue;

        const filteredOptions = this.props.searchFilter(this.state.searchValue, options);

        ((filteredOptions.length && filteredOptions) || options).forEach((opt) => {
            const { value, key, label, labelNative } = opt;
            const modifier = value === currentValue && isOpened ? 'selected' : isOpened ? 'opened' : undefined;

            if (value === currentValue) {
                selectedLabel = label;
            }
            customOptions.push(
                <li
                    data-value={value}
                    key={key || value}
                    onClick={() => this.selectOption(value)}
                    className={bem('option', modifier)}
                    onKeyDown={(e) => this.handleItemKeyDown(e, value)}
                    tabIndex="0"
                    role="option"
                >
                    {label}
                </li>,
            );

            nativeOptions.push(
                <option value={value} key={key || value} className={bem('option', modifier)}>
                    {labelNative || label}
                </option>,
            );
        });

        return {
            nativeOptions,
            customOptions,
            selectedLabel,
        };
    }

    selectOption(value) {
        if (this.props.value !== value) {
            this.props.onChange(value);
        }
    }

    render() {
        const {
            emptyLabel,
            className,
            selectRef,
            type,
            hasError,
            options,
            value,
            onChange,
            promptError,
            disabled,
            numOfListElementsDisplayed, // eslint-disable-line no-unused-vars
            onDropdown, // eslint-disable-line no-unused-vars
            openOnMount, // eslint-disable-line no-unused-vars
            setNoSizeNotification, // eslint-disable-line no-unused-vars
            noSizeNotification, // eslint-disable-line no-unused-vars
            searchFilter, // eslint-disable-line no-unused-vars
            searchPlaceholder,
            enableSearch,
            ...rest
        } = this.props;
        const { openedHeight } = this.state;
        // we have placeholder text and nothing has been selected yet, we are on the placeholder text.
        const placeholderText = emptyLabel && value !== undefined ? 'placeholderText' : 'selectableOption';
        const disabledSelect = disabled ? 'disabled' : '';
        const errorClass = hasError ? 'selectWithError' : undefined;
        const isOpened = this.state.open ? 'opened' : undefined;
        const isPromptError = promptError ? 'selectWithError' : undefined;
        const noLabelModifier = emptyLabel ? '' : 'noLabel';
        const { nativeOptions, customOptions, selectedLabel } = this.buildOptions(options, value, this.state.open);
        const defaultLabel = this.state.open ? emptyLabel : selectedLabel || emptyLabel;

        return (
            <div className={bem(null, [isOpened, type, disabledSelect], className)} ref={(node) => (this.node = node)}>
                <div className={bem('customizedSelect', isPromptError)} onClick={this.toggle} ref={selectRef}>
                    {emptyLabel && (
                        <div
                            className={bem('label', [isOpened, errorClass, isPromptError])}
                            ref={(node) => (this.listLabelElement = node)}
                            tabIndex="0"
                            onKeyDown={(e) => this.handleTogglerKeyDown(e)}
                            aria-expanded={isOpened}
                            aria-owns={this.list}
                            aria-haspopup="listbox"
                            aria-label={defaultLabel}
                            role="combobox"
                        >
                            {defaultLabel}
                        </div>
                    )}
                    {isOpened && enableSearch && (
                        <Search
                            value={this.state.searchValue}
                            onSearch={(searchValue) => this.setState({ searchValue })}
                            placeholder={searchPlaceholder || ''}
                            handleItemKeyDown={this.handleItemKeyDown}
                        />
                    )}
                    <ul
                        className={bem('field', [type, isOpened, noLabelModifier, 'custom', isPromptError, errorClass])}
                        ref={(node) => (this.list = node)}
                        style={isOpened && { maxHeight: openedHeight }}
                        role="listbox"
                    >
                        {customOptions}
                    </ul>
                </div>

                {/* Mobile and Tablet only using display:block; */}
                <select
                    className={bem('field', [type, placeholderText, 'nativeSelect', isPromptError, errorClass])}
                    value={value || 'emptyLabel'}
                    onChange={(event) => onChange(event.target.value)}
                    ref={(node) => {
                        typeof selectRef === 'function' && selectRef(node);
                        this.mobileSelect = node;
                    }}
                    {...rest}
                >
                    {emptyLabel && (
                        <option value="emptyLabel" disabled>
                            {emptyLabel}
                        </option>
                    )}

                    {nativeOptions}
                </select>
            </div>
        );
    }
}

export const defaultSearchFilter = (searchValue, listData) => {
    /*
    Search should perform 'starts with' check (not 'contains'), e.g. 'str' should not return 'Austria'.
    For multi-word values search should check all words, e.g. 'sta' should return 'United States'.
    Search should be case-insensitive, e.g. 'uNi' should return 'United Kingdom'.
    Search should ignore leading and trailing whitespaces in the search term, e.g. '(space)uni(space)' should still return 'United Kingdom'.
    */
    const searchValueLower = searchValue ? normalizeStringHelper(searchValue.toLowerCase().trim()) : normalizeStringHelper(searchValue);
    return searchValueLower
        ? listData.filter(({ label, value }) => {
              const valueLower = normalizeStringHelper(value.toLowerCase());
              const valueLowerWords = valueLower.split(' ');

              const labelLower = normalizeStringHelper(label.toLowerCase());
              const labelLowerWords = labelLower.split(' ');

              return (
                  valueLower.startsWith(searchValueLower) ||
                  valueLowerWords.some((valueLowerWord) => valueLowerWord.startsWith(searchValueLower)) ||
                  labelLower.startsWith(searchValueLower) ||
                  labelLowerWords.some((labelWord) => normalizeStringHelper(labelWord).startsWith(searchValueLower))
              );
          })
        : [];
};

CombinedSelect.defaultProps = {
    numOfListElementsDisplayed: 10,
    setNoSizeNotification: () => {},
    enableSearch: false,
    searchFilter: defaultSearchFilter,
};

CombinedSelect.propTypes = {
    emptyLabel: PropTypes.string,
    className: PropTypes.string,
    children: PropTypes.node,
    selectRef: PropTypes.func,
    type: PropTypes.string,
    hasError: PropTypes.bool,
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.string,
            key: PropTypes.string,
            label: PropTypes.string || PropTypes.node,
            labelNative: PropTypes.string,
        }),
    ),
    numOfListElementsDisplayed: PropTypes.number,
    onDropdown: PropTypes.func,
    onClose: PropTypes.func,
    promptError: PropTypes.bool,
    disabled: PropTypes.bool,
    openOnMount: PropTypes.bool,
    setNoSizeNotification: PropTypes.func,
    noSizeNotification: PropTypes.string,
    enableSearch: PropTypes.bool,
    searchFilter: PropTypes.func,
};

export default CombinedSelect;
